import type { FrameworkConfig, ProjectConfig } from "../../types/types.js"
import { BaseFramework } from "../base-framework.js"

const vanillaConfig: FrameworkConfig = {
  defaultMeta: "none",
  displayName: "Vanilla JavaScript",
  metaFrameworks: ["none"],
  name: "vanilla",
  starter: {
    args: (config: ProjectConfig) => {
      const args: Array<string> = []
      args.push("--template", config.typescript ? "vanilla-ts" : "vanilla")
      return args
    },
    command: "create-vite",
  },
}

export class VanillaFramework extends BaseFramework {
  constructor() {
    super(vanillaConfig)
  }

  getStarterCommand(projectConfig: ProjectConfig): string {
    const { projectName, typescript } = projectConfig
    const template = typescript ? "vanilla-ts" : "vanilla"

    return `npm create vite@latest ${projectName} -- --template ${template}`
  }

  getPostInstallTasks(
    projectConfig: ProjectConfig,
  ): Array<() => Promise<void>> {
    const tasks: Array<() => Promise<void>> = []

    tasks.push(async () => {
      // Vanilla specific setup
      await this.updatePackageJson(projectConfig, {
        scripts: {
          build: "vite build",
          dev: "vite",
          preview: "vite preview",
        },
      })

      // Create a better project structure for vanilla projects
      await this.createProjectStructure(projectConfig)
    })

    return tasks
  }

  private async createProjectStructure(
    projectConfig: ProjectConfig,
  ): Promise<void> {
    if (projectConfig.dryRun) {
      return
    }

    const { mkdirSync, writeFileSync, existsSync } = await import("fs")
    const { join } = await import("path")

    // Create directories
    const dirs = ["src/js", "src/css", "src/assets", "src/components"]
    for (const dir of dirs) {
      const dirPath = join(projectConfig.projectPath, dir)
      if (!existsSync(dirPath)) {
        mkdirSync(dirPath, { recursive: true })
      }
    }

    // Create a sample component structure
    const componentExample = projectConfig.typescript
      ? `// src/components/counter.ts
export class Counter {
  private count = 0;
  private element: HTMLElement;
  private displayElement: HTMLElement;

  constructor(element: HTMLElement) {
    this.element = element;
    this.render();
    this.attachEventListeners();
  }

  private render(): void {
    this.element.innerHTML = \`
      <div class="counter">
        <h2>Counter Component</h2>
        <div class="counter-display">Count: <span id="count-display">0</span></div>
        <div class="counter-buttons">
          <button id="decrement">-</button>
          <button id="increment">+</button>
          <button id="reset">Reset</button>
        </div>
      </div>
    \`;
    
    this.displayElement = this.element.querySelector('#count-display')!;
  }

  private attachEventListeners(): void {
    const incrementBtn = this.element.querySelector('#increment');
    const decrementBtn = this.element.querySelector('#decrement');
    const resetBtn = this.element.querySelector('#reset');

    incrementBtn?.addEventListener('click', () => this.increment());
    decrementBtn?.addEventListener('click', () => this.decrement());
    resetBtn?.addEventListener('click', () => this.reset());
  }

  private increment(): void {
    this.count++;
    this.updateDisplay();
  }

  private decrement(): void {
    this.count--;
    this.updateDisplay();
  }

  private reset(): void {
    this.count = 0;
    this.updateDisplay();
  }

  private updateDisplay(): void {
    this.displayElement.textContent = this.count.toString();
  }
}`
      : `// src/components/counter.js
export class Counter {
  constructor(element) {
    this.count = 0;
    this.element = element;
    this.render();
    this.attachEventListeners();
  }

  render() {
    this.element.innerHTML = \`
      <div class="counter">
        <h2>Counter Component</h2>
        <div class="counter-display">Count: <span id="count-display">0</span></div>
        <div class="counter-buttons">
          <button id="decrement">-</button>
          <button id="increment">+</button>
          <button id="reset">Reset</button>
        </div>
      </div>
    \`;
    
    this.displayElement = this.element.querySelector('#count-display');
  }

  attachEventListeners() {
    const incrementBtn = this.element.querySelector('#increment');
    const decrementBtn = this.element.querySelector('#decrement');
    const resetBtn = this.element.querySelector('#reset');

    incrementBtn?.addEventListener('click', () => this.increment());
    decrementBtn?.addEventListener('click', () => this.decrement());
    resetBtn?.addEventListener('click', () => this.reset());
  }

  increment() {
    this.count++;
    this.updateDisplay();
  }

  decrement() {
    this.count--;
    this.updateDisplay();
  }

  reset() {
    this.count = 0;
    this.updateDisplay();
  }

  updateDisplay() {
    this.displayElement.textContent = this.count;
  }
}`

    const ext = projectConfig.typescript ? "ts" : "js"
    writeFileSync(
      join(projectConfig.projectPath, `src/components/counter.${ext}`),
      componentExample,
    )
  }
}
